import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, VIEW, EDIT,
    INCREASE_TO, DECREASE_TO, STAY_ABOVE, STAY_BELOW, ACHIEVED,
    HARD, HARD_SOFT,
    DATE_FORMAT,
} from "const"
import { OkrKeyResultCreate } from "server/models"
import { okrKeyResultsRoute } from "client/parts/routes"
import {
    StateType,
    useInput,
    replaceHistoryState,
    hasAccess,
} from "client/utils"
import {
    PRM_ACCOUNT_SETTINGS,
    StoreType,
    OkrKeyResultObjectProps,
    OkrKeyResultValidation,

    SaveOkrKeyResult,

    setOkrKeyResultEdit,
    setOkrKeyResultReloadEdit,
    setOkrObjectiveReloadList,
    addModal,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Help,
    Input,
    Textarea,
    SliderSwitcher,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    UserDropdownList,
    Delimiter,
    TagEditor,
    DatePicker,
    Button,
    SpinnerIcon,
} from "client/components"

import EditOkrMetric from "client/pages/settings/okr/metric/edit"
import {
    MetricInput,
    KeyResultDeadlineWrapper,
    KeyResultDeadlineDescription,
} from "../styled"

let isMounted: boolean

const Form = ({
    objective,
    keyResult,
}: OkrKeyResultObjectProps) => {
    const session = useSelector((state: StoreType) => state.okrSession.sessionView)
    const objectiveBaseRoute = useSelector((state: StoreType) => state.okrObjective.baseRoute)
    const objectiveRoute = useSelector((state: StoreType) => state.okrObjective.objectiveRoute)
    const metricList = useSelector((state: StoreType) => state.okrMetric.metricList)
    const metricIds = useSelector((state: StoreType) => state.okrMetric.metricIds)

    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [title, setTitle] = useInput(keyResult.title)
    const [description, setDescription] = useInput(keyResult.description)
    const [type, setType] = useState(keyResult.type)
    const [metricId, setMetricId] = useState(keyResult.metricId)
    const [useCriticalTarget, setUseCriticalTarget] = useState(keyResult.useCriticalTarget)
    const [targetNumber, setTargetNumber] = useState(keyResult.targetNumber !== null ? keyResult.targetNumber.toString() : "")
    const [criticalNumber, setCriticalNumber] = useState(keyResult.criticalNumber !== null ? keyResult.criticalNumber.toString() : "")
    const [initialNumber, setInitialNumber] = useState(keyResult.initialNumber !== null ? keyResult.initialNumber.toString() : "")
    const [deadlineType, setDeadlineType] = useState(keyResult.deadlineType)
    const [deadlineHard, setDeadlineHard] = useState(keyResult.deadlineHard ? new Date(keyResult.deadlineHard) : null)
    const [deadlineSoft, setDeadlineSoft] = useState(keyResult.deadlineSoft ? new Date(keyResult.deadlineSoft) : null)

    const [owners, setOwners] = useState<number[]>([])
    const [tags, setTags] = useState<number[]>([])
    const [validation, setValidation] = useState<OkrKeyResultValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        if (keyResult.id) {
            if (keyResult.owners) {
                const userIds: number[] = []
                keyResult.owners.forEach((row) => {
                    userIds.push(row.userId)
                })
                setOwners(userIds)
            }
            if (keyResult.tags) {
                const tagIds: number[] = []
                keyResult.tags.forEach((row) => {
                    tagIds.push(row.tagId)
                })
                setTags(tagIds)
            }
        } else {
            setOwners([userData.userId])
        }
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        // this page can be opened only via modal
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (!owners.length) return

        // check for empty fields
        if (type !== ACHIEVED) {
            let newValidation: OkrKeyResultValidation = {}
            if (targetNumber.length === 0) {
                newValidation = {
                    ...newValidation,
                    targetNumber: lang.validationFieldRequired,
                }
            }
            if (initialNumber.length === 0) {
                newValidation = {
                    ...newValidation,
                    initialNumber: lang.validationFieldRequired,
                }
            }
            if (Object.keys(newValidation).length > 0) {
                setValidation(newValidation)
                return
            }
        }

        // validate values according to the type
        const targetNumberParsed = parseFloat(targetNumber)
        const initialNumberParsed = parseFloat(initialNumber)
        const criticalNumberParsed = criticalNumber.length > 0 ? parseFloat(criticalNumber) : null

        if (type === INCREASE_TO && initialNumberParsed >= targetNumberParsed) {
            setValidation({ initialNumber: lang.initialNumberLowerTarget })
            return
        }
        if (type === DECREASE_TO && initialNumberParsed <= targetNumberParsed) {
            setValidation({ initialNumber: lang.initialNumberGreaterTarget })
            return
        }
        if (type === STAY_ABOVE && criticalNumberParsed !== null && criticalNumberParsed >= targetNumberParsed) {
            setValidation({ criticalNumber: lang.criticalNumberLowerTarget })
            return
        }
        if (type === STAY_BELOW && criticalNumberParsed !== null && criticalNumberParsed <= targetNumberParsed) {
            setValidation({ criticalNumber: lang.criticalNumberGreaterTarget })
            return
        }

        // save
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        let newKeyResultData: OkrKeyResultCreate = {
            sessionId: keyResult.sessionId || session.id,
            objectiveId: keyResult.objectiveId || objective.id,
            title,
            description,
            type,
            metricId,
            targetNumber: targetNumberParsed,
            initialNumber: initialNumberParsed,
            useCriticalTarget: (type === STAY_ABOVE || type === STAY_BELOW) && useCriticalTarget && criticalNumberParsed !== null,
            criticalNumber: type === STAY_ABOVE || type === STAY_BELOW ? criticalNumberParsed : null,
            order: keyResult.order, // existing order or null from initKeyResult
            deadlineType,
            deadlineHard,
            deadlineSoft,
        }

        if (!keyResult.id) {
            if (type === ACHIEVED) {
                newKeyResultData = {
                    ...newKeyResultData,
                    metricId: null,
                    initialNumber: 0,
                    targetNumber: 1,
                    actualNumber: 0,
                }
            } else {
                newKeyResultData = {
                    ...newKeyResultData,
                    actualNumber: initialNumberParsed,
                }
            }
        } else {
            // changed from any type to ACHIEVED
            if (keyResult.type !== type && type === ACHIEVED) {
                newKeyResultData = {
                    ...newKeyResultData,
                    metricId: null,
                    initialNumber: 0,
                    targetNumber: 1,
                    actualNumber: 0,
                }
            }

            // changed from ACHIEVED to any other type
            if (keyResult.type !== type && keyResult.type === ACHIEVED) {
                newKeyResultData = {
                    ...newKeyResultData,
                    actualNumber: initialNumberParsed,
                }
            }
        }

        SaveOkrKeyResult(
            keyResult.id,
            newKeyResultData,
            owners,
            tags,
            lang,
        ).then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newKeyResultId) {
                    const baseRoute = `${objectiveBaseRoute}${objectiveRoute}/${VIEW}/${objective.id}${okrKeyResultsRoute}`
                    replaceHistoryState(`${baseRoute}/${EDIT}/${res.newKeyResultId}`)
                }
            }
            if (res.validation) return

            dispatch(setOkrKeyResultEdit({
                id: res.newKeyResultId || keyResult.id,
                ...newKeyResultData,
                order: res.newOrder,
                owners: res.owners,
                tags: res.tags,
            }))

            // reload main objective list
            dispatch(setOkrObjectiveReloadList(false))
            setTimeout(() => {
                dispatch(setOkrObjectiveReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setOkrKeyResultReloadEdit(false))
                setTimeout(() => {
                    dispatch(setOkrKeyResultReloadEdit(true))
                }, 0)
            }
        })
    }

    const selectedMetric = metricList[metricId] || null

    let selectedType = ""
    if (type === INCREASE_TO) selectedType = lang.shouldIncreaseTo
    if (type === DECREASE_TO) selectedType = lang.shouldDecreaseTo
    if (type === STAY_ABOVE) selectedType = lang.shouldStayAbove
    if (type === STAY_BELOW) selectedType = lang.shouldStayBelow
    if (type === ACHIEVED) selectedType = lang.achievedOrNot

    let selectedDeadlineType = ""
    if (deadlineType === null) selectedDeadlineType = lang.noCustomDeadline
    if (deadlineType === HARD) selectedDeadlineType = lang.hardDeadline
    if (deadlineType === HARD_SOFT) selectedDeadlineType = lang.hardSoftDeadline

    const minDeadlineSoft = new Date(session.dateStart)
    const minDeadlineHard = new Date(session.dateStart)
    minDeadlineHard.setDate(minDeadlineHard.getDate() + 1)

    const maxDeadlineHard = new Date(session.dateEnd)
    maxDeadlineHard.setDate(maxDeadlineHard.getDate() - 1)

    const maxDeadlineSoft = new Date(deadlineHard || maxDeadlineHard)
    maxDeadlineSoft.setDate(maxDeadlineSoft.getDate() - 1)

    const editAccountSettingsAccess = hasAccess(PRM_ACCOUNT_SETTINGS, EDIT)

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label flex>
                            {lang.title}
                            <Help text={lang.keyResultTitleDescription} />
                        </Label>
                        <Input type="text" value={title} onChange={setTitle} />
                        <Feedback>{validation && validation.title}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label flex>
                            {lang.keyResultType}
                            <Help text={lang.keyResultTypeDescription} />
                        </Label>
                        <SelectDropdown title={selectedType}>
                            <SelectDropdownItem onClick={() => setType(INCREASE_TO)}>{lang.shouldIncreaseTo}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(DECREASE_TO)}>{lang.shouldDecreaseTo}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(STAY_ABOVE)}>{lang.shouldStayAbove}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(STAY_BELOW)}>{lang.shouldStayBelow}</SelectDropdownItem>
                            <SelectDropdownItem onClick={() => setType(ACHIEVED)}>{`${lang.achievedOrNot} (100%/0%)`}</SelectDropdownItem>
                        </SelectDropdown>
                    </FormGroup>

                    {
                        type !== ACHIEVED && (
                            <FormGroup>
                                <Label flex>
                                    {lang.unit}
                                    <Help text={lang.unitDesription} />
                                </Label>
                                <SelectDropdown title={selectedMetric ? `${selectedMetric.name} (${selectedMetric.symbol})` : lang.notSet}>
                                    <SelectDropdownItem onClick={() => setMetricId(null)}>
                                        {lang.notSet}
                                    </SelectDropdownItem>
                                    {
                                        metricIds.map((id) => (
                                            <SelectDropdownItem
                                                key={id}
                                                onClick={() => setMetricId(id)}
                                            >
                                                {`${metricList[id].name} (${metricList[id].symbol})`}
                                            </SelectDropdownItem>
                                        ))
                                    }
                                    {
                                        editAccountSettingsAccess && (
                                            <SelectDropdownItem
                                                onClick={() => dispatch(addModal({
                                                    key: "addMetric",
                                                    content: <EditOkrMetric
                                                        metricId={0}
                                                        onCreate={(newMetricId) => setMetricId(newMetricId)}
                                                    />,
                                                }))}
                                            >
                                                {lang.createCustomUnit}
                                            </SelectDropdownItem>
                                        )
                                    }
                                </SelectDropdown>
                                <Feedback>{validation && validation.metricId}</Feedback>
                            </FormGroup>
                        )
                    }
                    {
                        type !== ACHIEVED && (
                            <FormGroup>
                                <Label flex>
                                    {lang.targetNumber}
                                    <Help text={lang.targetNumberDescription} />
                                </Label>
                                <MetricInput
                                    metric={selectedMetric ? selectedMetric.symbol : null}
                                    metricAfter={selectedMetric ? selectedMetric.displayAfter : null}
                                    value={targetNumber}
                                    onChange={(e) => setTargetNumber(e.target.value)}
                                />
                                <Feedback>{validation && validation.targetNumber}</Feedback>
                            </FormGroup>
                        )
                    }

                    {
                        (type === STAY_ABOVE || type === STAY_BELOW) && (
                            <FormGroup style={{
                                marginTop: "8px",
                                marginBottom: "16px",
                            }}
                            >
                                <SliderSwitcher
                                    active={useCriticalTarget}
                                    title={lang.useCriticalTarget}
                                    onClick={() => {
                                        setUseCriticalTarget(!useCriticalTarget)
                                        setCriticalNumber("")
                                    }}
                                />
                            </FormGroup>
                        )
                    }

                    {
                        (type === STAY_ABOVE || type === STAY_BELOW)
                        && useCriticalTarget && (
                            <FormGroup>
                                <Label flex>
                                    {lang.criticalNumber}
                                    <Help text={lang.criticalNumberDescription} />
                                </Label>
                                <MetricInput
                                    metric={selectedMetric ? selectedMetric.symbol : null}
                                    metricAfter={selectedMetric ? selectedMetric.displayAfter : null}
                                    value={criticalNumber}
                                    onChange={(e) => setCriticalNumber(e.target.value)}
                                />
                                <Feedback>{validation && validation.criticalNumber}</Feedback>
                            </FormGroup>
                        )
                    }

                    {
                        type !== ACHIEVED && (
                            <FormGroup>
                                <Label flex>
                                    {lang.initialNumber}
                                    <Help text={lang.initialNumberDescription} />
                                </Label>
                                <MetricInput
                                    metric={selectedMetric ? selectedMetric.symbol : null}
                                    metricAfter={selectedMetric ? selectedMetric.displayAfter : null}
                                    value={initialNumber}
                                    onChange={(e) => setInitialNumber(e.target.value)}
                                />
                                <Feedback>{validation && validation.initialNumber}</Feedback>
                            </FormGroup>
                        )
                    }

                    {
                        (type !== STAY_ABOVE && type !== STAY_BELOW) && (
                            <FormGroup>
                                <Label flex>
                                    {lang.deadline}
                                    <Help text={lang.deadlineOkrDescription} />
                                </Label>
                                <SelectDropdown title={selectedDeadlineType}>
                                    <SelectDropdownItem onClick={() => {
                                        setDeadlineType(null)
                                        setDeadlineHard(null)
                                        setDeadlineSoft(null)
                                    }}
                                    >
                                        <KeyResultDeadlineWrapper>
                                            <span>{lang.noCustomDeadline}</span>
                                            <KeyResultDeadlineDescription>{lang.noCustomDeadlineDescription}</KeyResultDeadlineDescription>
                                        </KeyResultDeadlineWrapper>
                                    </SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => {
                                        setDeadlineType(HARD)
                                        if (deadlineHard === null) setDeadlineHard(maxDeadlineHard)
                                    }}
                                    >
                                        <KeyResultDeadlineWrapper>
                                            <span>{lang.hardDeadline}</span>
                                            <KeyResultDeadlineDescription>{lang.hardDeadlineDescription}</KeyResultDeadlineDescription>
                                        </KeyResultDeadlineWrapper>
                                    </SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => {
                                        setDeadlineType(HARD_SOFT)
                                        if (deadlineHard === null) setDeadlineHard(maxDeadlineHard)
                                        if (deadlineSoft === null) setDeadlineSoft(maxDeadlineSoft)
                                    }}
                                    >
                                        <KeyResultDeadlineWrapper>
                                            <span>{lang.hardSoftDeadline}</span>
                                            <KeyResultDeadlineDescription>{lang.hardSoftDeadlineDescription}</KeyResultDeadlineDescription>
                                        </KeyResultDeadlineWrapper>
                                    </SelectDropdownItem>
                                </SelectDropdown>
                            </FormGroup>
                        )
                    }

                    {
                        (type !== STAY_ABOVE && type !== STAY_BELOW)
                        && deadlineType === HARD_SOFT && (
                            <FormGroup>
                                <Label>{lang.softDeadline}</Label>
                                <DatePicker
                                    selected={deadlineSoft}
                                    onChange={(date: Date) => {
                                        if (!date) setDeadlineSoft(deadlineSoft)
                                        else setDeadlineSoft(date)
                                    }}
                                    minDate={minDeadlineSoft}
                                    maxDate={maxDeadlineSoft}
                                    dateFormat={DATE_FORMAT}
                                />
                            </FormGroup>
                        )
                    }
                    {
                        (type !== STAY_ABOVE && type !== STAY_BELOW)
                        && (deadlineType === HARD || deadlineType === HARD_SOFT) && (
                            <FormGroup>
                                <Label>{lang.hardDeadline}</Label>
                                <DatePicker
                                    selected={deadlineHard}
                                    onChange={(date: Date) => {
                                        if (!date) {
                                            setDeadlineHard(deadlineHard)
                                        } else {
                                            setDeadlineHard(date)
                                            if (deadlineSoft >= date) {
                                                const newDeadlineSoft = new Date(date)
                                                newDeadlineSoft.setDate(newDeadlineSoft.getDate() - 1)
                                                setDeadlineSoft(newDeadlineSoft)
                                            }
                                        }
                                    }}
                                    minDate={minDeadlineHard}
                                    maxDate={maxDeadlineHard}
                                    dateFormat={DATE_FORMAT}
                                />
                            </FormGroup>
                        )
                    }

                    <FormGroup>
                        <Label>{lang.owners}</Label>
                        <UserDropdownList
                            selectedIds={owners}
                            listIds={(accountUserList && accountUserList[userData.accountId]) || []}
                            title={lang.addOwners}
                            onDelete={setOwners}
                            onAdd={setOwners}
                        />
                        <Feedback>{owners.length === 0 ? lang.validationFieldRequired : null}</Feedback>
                    </FormGroup>
                    <Delimiter />

                    <FormGroup>
                        <Label>{lang.tags}</Label>
                        <TagEditor
                            selectedIds={tags}
                            onChange={(tagIds) => setTags(tagIds)}
                        />
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
